import { Button } from "@/components/ui/button";
import { Download, ChevronDown, CheckCircle2, Phone, Mail, Globe, MapPin, Building2, Brain, ShieldCheck, Activity, Database, Server, Layers, Cpu, LineChart, Cloud, Zap, ArrowRight, ShoppingBag, Tag, CreditCard, Heart, Printer } from "lucide-react";
import { SEO } from "@/components/SEO";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { downloadAsPDF, downloadAsImage } from "@/lib/downloadUtils";

export const RetailCapabilityStatement = () => {
    return (
        <div className="min-h-screen bg-slate-50 font-sans print:bg-white text-slate-900">
            <SEO
                title="Retail Capability Statement | RSA Tech Group"
                description="Unifying Unified Commerce. Salesforce transformations, AI personalization, and omnichannel retail solutions."
                image="/retail_capability_hero.png"
            />
            {/* Download/Print Control Bar (Hidden in Print) */}
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center print:hidden sticky top-0 z-50 shadow-md">
                <div className="font-bold text-lg">RSA Tech Group Capability Statement - Retail & E-Commerce</div>
                <div className="flex items-center gap-3">
                    <Button
                        onClick={() => window.print()}
                        variant="outline"
                        className="bg-transparent text-white border-white hover:bg-white/10 font-bold"
                    >
                        <Printer className="w-4 h-4 mr-2" />
                        Print
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="bg-white text-slate-900 hover:bg-slate-100 font-bold border-none">
                                <Download className="w-4 h-4 mr-2" />
                                Download
                                <ChevronDown className="w-4 h-4 ml-2" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => downloadAsPDF("capability-statement", "Retail_Capability_Statement_RSA_Tech")}>
                                Download as PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadAsImage("capability-statement", "Retail_Capability_Statement_RSA_Tech", "png")}>
                                Download as PNG
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadAsImage("capability-statement", "Retail_Capability_Statement_RSA_Tech", "jpeg")}>
                                Download as JPG
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* A4 Styled Container - One Page Optimized */}
            <div id="capability-statement" className="max-w-[210mm] h-[297mm] mx-auto bg-white shadow-2xl my-8 print:shadow-none print:my-0 print:w-[210mm] print:h-[297mm] overflow-hidden relative flex flex-col">

                {/* 1. Compact Header */}
                <header className="relative h-[160px] shrink-0 overflow-hidden">
                    <img
                        src="/retail_capability_hero.png"
                        alt="Retail Technology"
                        className="absolute inset-0 w-full h-full object-cover object-center opacity-95 brightness-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-transparent"></div>

                    <div className="relative z-10 p-6 h-full flex flex-col justify-between">
                        <div className="flex justify-between items-start w-full">
                            <img src="/rsa-logo.png" alt="RSA Tech Group" className="h-12 w-auto object-contain" />

                            <div className="flex flex-col items-end gap-1">
                                <div className="bg-white/90 backdrop-blur-md px-6 py-2 rounded-lg shadow-sm border border-white/50">
                                    <h1 className="text-sm font-extrabold uppercase tracking-widest text-slate-900">Capability Statement</h1>
                                </div>
                                <div className="bg-rose-100/80 backdrop-blur-md px-3 py-1 rounded-full border border-rose-200 shadow-sm">
                                    <p className="text-rose-900 font-extrabold text-[10px] tracking-widest uppercase">Retail & E-Commerce</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-auto max-w-xl">
                            <h2 className="text-3xl font-extrabold leading-none text-slate-900 tracking-tighter mb-2 drop-shadow-sm">
                                Unifying <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-pink-600">Commerce & CX</span>
                            </h2>
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-orange-500"></div>
                                <div className="h-1 w-24 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full"></div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* 2. Core Content - Compact */}
                <main className="px-8 py-4 space-y-4 relative z-10 flex-grow">

                    {/* Company Overview Section */}
                    <section className="relative rounded-2xl overflow-hidden p-5 border border-slate-100 shadow-sm">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#fff1f2] via-[#f8fafc] to-[#fce7f3]"></div>

                        <div className="relative z-10 grid grid-cols-12 gap-6 items-start">
                            <div className="col-span-12 md:col-span-7 space-y-3">
                                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide flex items-center gap-2">
                                    <div className="p-1.5 bg-rose-600 rounded text-white shadow-sm">
                                        <ShoppingBag className="w-3.5 h-3.5" />
                                    </div>
                                    Company Overview
                                </h3>
                                <div className="space-y-2 text-slate-700 text-xs font-medium leading-relaxed text-justify">
                                    <p>
                                        RSA Tech Group is an Enterprise Solutions Partner (MSP) empowering retailers to deliver unified commerce experiences. We bridge the gap between digital and physical channels, integrating complex ecosystems like Salesforce Commerce Cloud.
                                    </p>
                                    <p>
                                        From architecting headless commerce platforms to deploying AI-driven personalization, we ensure scalability during peak seasons and deliver measurable growth in conversion and value.
                                    </p>
                                </div>
                            </div>

                            {/* Key Differentiators */}
                            <div className="col-span-12 md:col-span-5 relative group">
                                <div className="absolute inset-0 bg-gradient-to-r from-rose-200 to-pink-200 rounded-xl blur opacity-30"></div>
                                <div className="relative bg-white/80 backdrop-blur-xl p-4 rounded-xl border border-white/50 shadow-sm ring-1 ring-rose-50">
                                    <h4 className="font-bold text-slate-900 mb-2 text-xs uppercase tracking-wider border-b border-slate-100 pb-1">Key Differentiators</h4>
                                    <ul className="grid grid-cols-1 gap-1.5">
                                        {[
                                            { icon: CheckCircle2, text: "Unified Commerce Execution" },
                                            { icon: Cloud, text: "Salesforce Cloud Expertise" },
                                            { icon: Layers, text: "Managed Retail Operations" },
                                            { icon: Brain, text: "AI Personalization" }
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-center gap-2 text-[10px] font-bold text-slate-700">
                                                <div className="w-4 h-4 rounded-full bg-rose-50 flex items-center justify-center text-rose-600 border border-rose-100">
                                                    <item.icon className="w-2.5 h-2.5" />
                                                </div>
                                                {item.text}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Representative Projects Section - 2 Column Grid */}
                    <section className="space-y-2">
                        <div className="flex items-center gap-2 px-1">
                            <div className="p-1 bg-rose-100 rounded text-rose-700"><Server className="w-3.5 h-3.5" /></div>
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Representative Projects</h3>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            {/* CASE STUDY 1: UNIFIED COMMERCE */}
                            <div className="relative rounded-xl p-0.5 bg-gradient-to-br from-rose-200 via-rose-100 to-white shadow-sm overflow-hidden">
                                <div className="bg-white rounded-[10px] p-3 relative z-10 h-full flex flex-col">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <div className="text-[9px] font-bold text-rose-600 uppercase tracking-widest mb-0.5">B2B & B2C</div>
                                            <h4 className="text-xs font-bold text-slate-900 leading-tight">Unified Ecosystem</h4>
                                        </div>
                                        <div className="px-2 py-0.5 rounded-full bg-rose-50 border border-rose-100 text-[8px] font-extrabold text-rose-700 uppercase tracking-wider">
                                            Multi-Cloud
                                        </div>
                                    </div>
                                    <div className="space-y-1.5 flex-grow">
                                        <div>
                                            <div className="text-[8px] uppercase font-bold text-slate-400">Challenge</div>
                                            <p className="text-slate-700 text-[10px] leading-tight">Disconnected storefronts; data silos.</p>
                                        </div>
                                        <div>
                                            <div className="text-[8px] uppercase font-bold text-slate-400">Solution</div>
                                            <p className="text-slate-700 text-[10px] leading-tight">Integrated Salesforce Commerce/Sales/Service.</p>
                                        </div>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                                        <div className="text-[9px] font-bold text-slate-500">Outcome</div>
                                        <div className="text-lg font-extrabold text-rose-600">35% <span className="text-[9px] font-medium text-slate-600">Faster Sales</span></div>
                                    </div>
                                </div>
                            </div>

                            {/* CASE STUDY 2: PERSONALIZATION */}
                            <div className="relative rounded-xl p-0.5 bg-gradient-to-br from-pink-200 via-pink-100 to-white shadow-sm overflow-hidden">
                                <div className="bg-white rounded-[10px] p-3 relative z-10 h-full flex flex-col">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <div className="text-[9px] font-bold text-pink-600 uppercase tracking-widest mb-0.5">Experience</div>
                                            <h4 className="text-xs font-bold text-slate-900 leading-tight">Personalization</h4>
                                        </div>
                                        <div className="px-2 py-0.5 rounded-full bg-pink-50 border border-pink-100 text-[8px] font-extrabold text-pink-700 uppercase tracking-wider">
                                            AI Driven
                                        </div>
                                    </div>
                                    <div className="space-y-1.5 flex-grow">
                                        <div>
                                            <div className="text-[8px] uppercase font-bold text-slate-400">Challenge</div>
                                            <p className="text-slate-700 text-[10px] leading-tight">Low conversion; static UX.</p>
                                        </div>
                                        <div>
                                            <div className="text-[8px] uppercase font-bold text-slate-400">Solution</div>
                                            <p className="text-slate-700 text-[10px] leading-tight">Unified Data Layer & Real-time AI.</p>
                                        </div>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                                        <div className="text-[9px] font-bold text-slate-500">Outcome</div>
                                        <div className="text-lg font-extrabold text-pink-600">22% <span className="text-[9px] font-medium text-slate-600">Cov. Lift</span></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* TWO COLUMN - Compact */}
                    <div className="grid grid-cols-2 gap-4 flex-grow">

                        {/* LEFT: Core Services */}
                        <section className="bg-gradient-to-b from-white to-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                                <div className="p-1 bg-rose-100 text-rose-700 rounded"><Layers className="w-3.5 h-3.5" /></div>
                                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-900">Core Services</h3>
                            </div>
                            <div className="space-y-2">
                                {[
                                    { icon: Cloud, title: "Salesforce Multi-Cloud", desc: "Commerce + Sales + Service." },
                                    { icon: Zap, title: "Unified Commerce", desc: "Single customer view." },
                                    { icon: Brain, title: "AI Personalization", desc: "Real-time intent scoring." },
                                    { icon: Tag, title: "Headless Commerce", desc: "Decoupled front-ends." },
                                    { icon: ShoppingBag, title: "Order Management", desc: "Optimizing fulfillment." },
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-2.5">
                                        <div className="mt-0.5 w-5 h-5 rounded bg-rose-50 flex items-center justify-center text-rose-600 shrink-0">
                                            <item.icon className="w-3 h-3" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-[10px] text-slate-900 leading-none mb-0.5">{item.title}</h4>
                                            <p className="text-[9px] text-slate-600 leading-none">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* RIGHT: Why Partner with RSA Tech Group */}
                        <section className="bg-[#0f172a] text-white p-4 rounded-xl border border-slate-800 relative overflow-hidden shadow-sm flex flex-col justify-center">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/20 rounded-full blur-[40px] -mr-10 -mt-10"></div>

                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700/50 relative z-10">
                                <div className="p-1 bg-rose-900/50 text-rose-400 rounded ring-1 ring-rose-500/30"><ShieldCheck className="w-3.5 h-3.5" /></div>
                                <h3 className="text-xs font-bold uppercase tracking-wide text-white">Why RSA Tech Group?</h3>
                            </div>

                            <div className="space-y-3 relative z-10">
                                {[
                                    { title: "Retail Ecosystem", desc: "Salesforce, Adobe, Shopify experts." },
                                    { title: "Customer-Centric", desc: "Aligned to AOV & Conversion." },
                                    { title: "Peak Season Reliability", desc: "100% uptime events." },
                                    { title: "Unified Data Vision", desc: "Golden Customer record." }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-2.5">
                                        <div className="mt-0.5"><CheckCircle2 className="w-3.5 h-3.5 text-rose-400" /></div>
                                        <div>
                                            <h4 className="font-bold text-[10px] text-rose-50 leading-none mb-0.5">{item.title}</h4>
                                            <p className="text-[9px] text-slate-400 leading-tight">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>

                    {/* Contact Footer - Ultra Compact */}
                    <footer className="relative overflow-hidden rounded-xl p-3 shadow-md shrink-0">
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0d0b24] via-[#16143d] to-[#360c0c]"></div>
                        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-l from-orange-900/20 to-transparent"></div>

                        <div className="relative z-10 flex flex-row justify-between items-center gap-4 text-[10px]">
                            <div className="flex items-center gap-2">
                                <img src="/rsa-logo.png" alt="RSA Tech Group" className="h-6 w-auto brightness-0 invert opacity-100" />
                            </div>

                            <div className="flex items-center gap-4 font-bold tracking-wide text-white/90">
                                <div className="flex items-center gap-1.5">
                                    <Mail className="w-3 h-3 text-orange-400" />
                                    <span>Info@rsatech.com</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Phone className="w-3 h-3 text-orange-400" />
                                    <span>469-927-3307</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Globe className="w-3 h-3 text-orange-400" />
                                    <span>www.rsatech.com</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <MapPin className="w-3 h-3 text-orange-400" />
                                    <span>2601 LittleElm Pkwy, Suite 303, LittleElm, TX 75068</span>
                                </div>
                            </div>
                        </div>
                    </footer>
                </main>

                <div className="h-1.5 w-full bg-gradient-to-r from-rose-900 via-pink-600 to-amber-600 shrink-0"></div>
            </div>
        </div>
    );
};
